import type { HandlerRecord } from '@vlabs/api/artemy/model';

export const isEnabled = (value: unknown): boolean => value === 1 || value === true;

export function formatRange(min?: number | null, max?: number | null): string {
  const hasMin = min !== undefined && min !== null;
  const hasMax = max !== undefined && max !== null;

  if (!hasMin && !hasMax) return '';
  if (!hasMin) return `– ${max}`;
  if (!hasMax) return `${min} –`;

  return `${min} – ${max}`;
}

export interface MatchFilterView {
  label?: string;
  similarityGte?: number;
  similarityLte?: number;
}

export interface FiltersView {
  gender?: number;
  ageGte?: number;
  ageLt?: number;
  liveness?: number[];
  deepfake?: number[];
  match?: MatchFilterView[];
}

const asNumber = (value: unknown): number | undefined => (
  typeof value === 'number' ? value : undefined
);

const asNumberArray = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const numbers = value.filter((item): item is number => typeof item === 'number');

  return numbers.length > 0 ? numbers : undefined;
};

export function extractFilters(filters: unknown): FiltersView {
  if (!filters || typeof filters !== 'object') return {};

  const record = filters as Record<string, unknown>;

  return {
    gender: asNumber(record.gender),
    ageGte: asNumber(record.age__gte),
    ageLt: asNumber(record.age__lt),
    liveness: asNumberArray(record.liveness),
    deepfake: asNumberArray(record.deepfake),
    match: Array.isArray(record.match)
      ? record.match.map((item) => {
        const match = item as Record<string, unknown>;

        return {
          label: typeof match.label === 'string' ? match.label : undefined,
          similarityGte: asNumber(match.similarity__gte),
          similarityLte: asNumber(match.similarity__lte),
        };
      })
      : undefined,
  };
}

export function hasFilters(filters: FiltersView): boolean {
  return filters.gender !== undefined
    || filters.ageGte !== undefined
    || filters.ageLt !== undefined
    || !!filters.liveness
    || !!filters.deepfake
    || !!filters.match;
}

export const CALLBACK_TYPE_LUNA_WS = 'luna-ws-notification';
export const CALLBACK_TYPE_KAFKA = 'kafka';
export const CALLBACK_TYPE_HTTP = 'http';

export const isLunaWsCallback = (callback: { type: string }): boolean => (
  callback.type === CALLBACK_TYPE_LUNA_WS
);

const anyEnabled = (values: unknown[]): boolean => values.some(isEnabled);

const hasQualityChecks = (checks: unknown): boolean => {
  if (!checks || typeof checks !== 'object') return false;

  return Object.values(checks).some((check) => {
    if (!check || typeof check !== 'object') return false;

    const record = check as Record<string, unknown>;

    if (isEnabled(record.estimate)) return true;

    return record.threshold !== undefined && record.threshold !== null;
  });
};

export function hasDetectionContent(handler: HandlerRecord): boolean {
  const detectPolicy = handler.policies.detect_policy;
  if (!detectPolicy) return false;

  const faceAttributes = [
    detectPolicy.estimate_head_pose,
    detectPolicy.estimate_emotions,
    detectPolicy.estimate_mask,
    detectPolicy.estimate_quality,
    detectPolicy.estimate_gaze,
    detectPolicy.estimate_glasses,
    detectPolicy.estimate_eyes_attributes,
    detectPolicy.estimate_mouth_attributes,
    detectPolicy.detect_landmarks68,
    detectPolicy.extract_exif,
  ];
  if (anyEnabled(faceAttributes)) return true;

  if (isEnabled(detectPolicy.estimate_liveness?.estimate)) return true;
  if (isEnabled(detectPolicy.estimate_deepfake?.estimate)) return true;

  const faceQuality = detectPolicy.face_quality;
  if (faceQuality && (
    isEnabled(faceQuality.estimate)
    || isEnabled(faceQuality.filter)
    || hasQualityChecks(faceQuality.checks)
  )) return true;

  if (isEnabled(detectPolicy.estimate_face_occlusion?.estimate)) return true;

  const bodyAttributes = detectPolicy.body_attributes;
  if (bodyAttributes && anyEnabled([
    bodyAttributes.estimate_basic_attributes,
    bodyAttributes.estimate_upper_body,
    bodyAttributes.estimate_lower_body,
    bodyAttributes.estimate_accessories,
  ])) return true;

  const peopleCount = detectPolicy.estimate_people_count;
  if (peopleCount && anyEnabled([
    peopleCount.estimate,
    peopleCount.people_coordinates,
  ])) return true;

  return false;
}

export function hasMatchContent(handler: HandlerRecord): boolean {
  return (handler.policies.match_policy ?? []).length > 0;
}

export function hasStorageContent(handler: HandlerRecord): boolean {
  const storagePolicy = handler.policies.storage_policy;
  if (!storagePolicy) return false;

  // store_* = 0 → политика выключена; undefined → не задана (карточку не показываем,
  // кроме luna-ws — он не зависит от store-флагов)
  if (storagePolicy.face_sample_policy && storagePolicy.face_sample_policy.store_sample !== 0) return true;
  if (storagePolicy.body_sample_policy && storagePolicy.body_sample_policy.store_sample !== 0) return true;
  if (storagePolicy.image_origin_policy && storagePolicy.image_origin_policy.store_image !== 0) return true;
  if (storagePolicy.event_policy && storagePolicy.event_policy.store_event !== 0) return true;

  return (storagePolicy.callbacks ?? []).some(isLunaWsCallback);
}

export function hasTagsContent(handler: HandlerRecord): boolean {
  return (handler.policies.conditional_tags_policy ?? []).length > 0;
}

export function hasCallbacksContent(handler: HandlerRecord): boolean {
  const callbacks = handler.policies.storage_policy?.callbacks ?? [];

  return callbacks.some((callback) => !isLunaWsCallback(callback));
}
